import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    AppBar,
    Box,
    Button,
    Card,
    CircularProgress,
    IconButton,
    LinearProgress,
    TextField,
    Typography,
} from '@material-ui/core';
import { Search, Add, Person, Phone, ChevronLeft, ChevronRight } from '@material-ui/icons';
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';

import Whatsapp from '../../components/Icons/Whatsapp';
import ViewDataEmpty from '../../components/ViewDataEmpty/ViewDataEmpty';
import { errorToast, formatNumber, routes } from '../../constants.js/misc';
import { setClientToEdit, setPageState, setRowsPerPageState } from '../../modules/clients';
import ImageDefaultClients from '../../assets/img/default/EmptyClients.png';
import { getClientList } from '../../services/clients';
import { AddClientModal } from '../HomePage/components/AddClientModal';
import { useDebounce } from '../../utils/app';
import useStyles from './ClientsPage.styles';

const toTelHref = (phone) => {
    const raw = String(phone || '').trim().replace(/\s/g, '');
    if (!raw) {
        return '';
    }
    const digits = raw.replace(/[^\d+]/g, '');
    if (!digits) {
        return '';
    }
    return digits.startsWith('+') ? `tel:${digits}` : `tel:+${digits.replace(/\D/g, '')}`;
};

const toWaDigits = (phone) => String(phone || '').replace(/\D/g, '');

const MOBILE_CLIENT_AVATAR_PALETTES = [
    { bg: '#ecfdf5', icon: '#047857' },
    { bg: '#fff7ed', icon: '#c2410c' },
    { bg: '#eff6ff', icon: '#1d4ed8' },
    { bg: '#fdf2f8', icon: '#be185d' },
    { bg: '#f5f3ff', icon: '#6d28d9' },
];

const hashAvatarPaletteIndex = (key) => {
    const s = String(key || '0');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = (hash << 5) - hash + s.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash) % MOBILE_CLIENT_AVATAR_PALETTES.length;
};

const getMobileClientAvatarPalette = (row) => {
    const key = `${row?.first_name ?? ''}|${row?.last_name ?? ''}|${row?.id ?? ''}`;
    return MOBILE_CLIENT_AVATAR_PALETTES[hashAvatarPaletteIndex(key)];
};

const formatStatCount = (n) =>
    new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(n) || 0);

const MobileClientsStatsSection = ({ classes, t, total }) => (
    <Box component="section" id="stats-section" className={classes.mobileStatsSection}>
        <Typography component="h2" className={classes.mobileStatsTitle}>
            {t('ClientPage.statsTitle')}
        </Typography>
        <Box className={classes.mobileStatsGridSingle}>
            <Box className={`${classes.mobileStatsCard} ${classes.mobileStatsCardSage}`}>
                <Typography
                    component="div"
                    className={`${classes.mobileStatsCardValue} ${classes.mobileStatsCardValueSage}`}
                >
                    {formatStatCount(total)}
                </Typography>
                <Typography
                    component="div"
                    className={`${classes.mobileStatsCardLabel} ${classes.mobileStatsCardLabelSage}`}
                >
                    {t('ClientPage.statsTotal')}
                </Typography>
            </Box>
        </Box>
    </Box>
);

const buildMobilePaginationItems = (pageIndex0, totalPages) => {
    const current = pageIndex0 + 1;
    if (totalPages <= 1) {
        return [{ type: 'page', value: 1 }];
    }
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => ({ type: 'page', value: i + 1 }));
    }
    const items = [];
    const pushPage = (n) => items.push({ type: 'page', value: n });
    const pushEllipsis = () => {
        if (items.length && items[items.length - 1].type !== 'ellipsis') {
            items.push({ type: 'ellipsis' });
        }
    };
    pushPage(1);
    if (current <= 3) {
        pushPage(2);
        pushPage(3);
        pushEllipsis();
        pushPage(totalPages);
    } else if (current >= totalPages - 2) {
        pushEllipsis();
        pushPage(totalPages - 2);
        pushPage(totalPages - 1);
        pushPage(totalPages);
    } else {
        pushEllipsis();
        pushPage(current - 1);
        pushPage(current);
        pushPage(current + 1);
        pushEllipsis();
        pushPage(totalPages);
    }
    return items;
};

const MobileClientCard = ({ row, classes, storeData, onNavigateDetail, onWhatsApp }) => {
    const { t } = useTranslation();
    const fullName = `${row?.first_name ?? ''} ${row?.last_name ?? ''}`.trim();
    const email = row?.email;
    const phoneDisplay = row?.phone_number;
    const telHref = toTelHref(row?.phone_number);
    const waDigits = toWaDigits(row?.phone_number);
    const canCall = Boolean(telHref);
    const canWa = Boolean(waDigits);
    const avatarPalette = getMobileClientAvatarPalette(row);

    return (
        <Card
            className={classes.mobileClientCardShell}
            elevation={0}
            component="article"
            role="listitem"
        >
            <Box className={classes.mobileClientCardRow}>
                <Box
                    className={classes.mobileClientAvatar}
                    style={{ backgroundColor: avatarPalette.bg }}
                    aria-hidden
                >
                    <Person style={{ color: avatarPalette.icon, fill: avatarPalette.icon }} />
                </Box>
                <Box className={classes.mobileClientMainCol}>
                    <Box className={classes.mobileClientTopRow}>
                        <Box className={classes.mobileClientTextBlock}>
                            <Typography
                                component={RouterLink}
                                to={routes.clientPage + '/' + row.id}
                                onClick={() => onNavigateDetail(row)}
                                className={classes.mobileClientNameLink}
                            >
                                {fullName || '—'}
                            </Typography>
                            {email ? (
                                <Typography component="span" className={classes.mobileClientSubline}>
                                    {email}
                                </Typography>
                            ) : null}
                            {phoneDisplay ? (
                                <Typography component="span" className={classes.mobileClientSubline}>
                                    {phoneDisplay}
                                </Typography>
                            ) : null}
                        </Box>
                        <Box className={classes.mobileClientActions}>
                            <IconButton
                                component={canCall ? 'a' : 'button'}
                                href={canCall ? telHref : undefined}
                                className={classes.mobileClientActionPhone}
                                size="small"
                                disabled={!canCall}
                                aria-label={`Llamar a ${fullName}`}
                            >
                                <Phone style={{ fontSize: 16, color: '#2563eb' }} />
                            </IconButton>
                            <IconButton
                                className={classes.mobileClientActionWhatsapp}
                                size="small"
                                disabled={!canWa}
                                aria-label={`WhatsApp a ${fullName}`}
                                onClick={() => onWhatsApp(fullName, row?.phone_number)}
                            >
                                <Whatsapp style={{ fontSize: 18, color: '#16a34a' }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box className={classes.mobileClientCardFooter}>
                <Typography component="span" className={classes.mobileClientOrdersLine}>
                    <span className={classes.mobileClientOrdersLineBold}>
                        {row?.quantity === 1
                            ? `${row?.quantity} pedido`
                            : `${row?.quantity} pedidos`}
                    </span>
                    {' · '}
                    {`${formatNumber(row?.total)} ${storeData?.currency ?? ''}`}
                </Typography>
                <Button
                    type="button"
                    size="small"
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to={routes.clientPage + '/' + row.id}
                    onClick={() => onNavigateDetail(row)}
                    className={classes.mobileClientVerBtn}
                    aria-label={`${t('ClientPage.mobileVerBtn')}: ${fullName || 'cliente'}`}
                >
                    {t('ClientPage.mobileVerBtn')}
                </Button>
            </Box>
        </Card>
    );
};

const ClientsPageMobile = ({
    storeId,
    initialPage,
    initialRowsPerPage,
    setClientToEdit,
    setPageState,
    setRowsPerPageState,
    storeData,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const classes = useStyles();

    const [gettingData, setGettingData] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const [page, setPage] = useState(initialPage);
    const [rows, setRows] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [totalRows, setTotalRows] = useState(0);
    const [filter, setFilter] = useState('');
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [summaryTotal, setSummaryTotal] = useState(0);

    const debouncedFilter = useDebounce(filter, 400);

    useEffect(() => {
        let cancelled = false;
        const loadSummary = async () => {
            try {
                const listRes = await getClientList(storeId, 1, 0, '', '', '', '', '');
                if (cancelled) {
                    return;
                }
                setSummaryTotal(+listRes.headers['x-total-count'] || 0);
            } catch {
                if (!cancelled) {
                    setSummaryTotal(0);
                }
            }
        };
        loadSummary();
        return () => {
            cancelled = true;
        };
    }, [storeId]);

    const handleSendWhatsapp = (name, phone) => {
        const digits = toWaDigits(phone);
        if (!digits) {
            return;
        }
        const messageUrl = `https://wa.me/${digits}?text=`;
        const message = `Hola, ${name}, Te contactamos de ${storeData?.name}`;
        window.open(`${messageUrl}${encodeURIComponent(message).replace(/%20/g, '+')}`);
    };

    const handleChangePage = (e, newPage) => {
        setPage(newPage);
        setPageState(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const next = +event.target.value;
        setPage(0);
        setPageState(0);
        setRowsPerPage(next);
        setRowsPerPageState(next);
    };

    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage) || 1);
    const rangeFrom = totalRows === 0 ? 0 : page * rowsPerPage + 1;
    const rangeTo = Math.min((page + 1) * rowsPerPage, totalRows);
    const canGoPrev = page > 0;
    const canGoNext = (page + 1) * rowsPerPage < totalRows;

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setPage(0);
        setPageState(0);
    };

    const handleAddClient = () => {
        setIsClientModalOpen(true);
    };

    useEffect(() => {
        setGettingData(true);
        getClientList(
            storeId,
            rowsPerPage,
            page,
            debouncedFilter.replace(/\+/g, ' ').trim(),
            '',
            '',
            '',
            ''
        )
            .then((res) => {
                setTotalRows(+res.headers['x-total-count'] || 0);
                setRows(
                    res.data.map((item) => ({
                        ...item,
                        showProducts: false,
                    }))
                );
                setGettingData(false);
                setFirstLoad(false);
            })
            .catch((err) => {
                console.log('Error ClientsPageMobile', err);
                setGettingData(false);
                setFirstLoad(false);
                enqueueSnackbar(t('ClientPage.tableError'), errorToast);
            });
    }, [storeId, rowsPerPage, page, debouncedFilter, enqueueSnackbar, t]);

    if (gettingData && firstLoad) {
        return (
            <Box
                style={{ height: '90vh', width: '100%' }}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (rows.length === 0 && !gettingData) {
        return (
            <Box className={classes.mobileRoot}>
                <AppBar position="sticky" className={classes.mobileHeader} elevation={0}>
                    <Box className={classes.mobileHeaderContent}>
                        <TextField
                            fullWidth
                            placeholder={t('ClientsDetail.placeholderSearch')}
                            variant="outlined"
                            size="small"
                            className={classes.mobileSearchBox}
                            InputProps={{
                                startAdornment: (
                                    <Search style={{ marginLeft: 6, marginRight: 8, color: '#999' }} />
                                ),
                            }}
                            value={filter}
                            onChange={handleFilterChange}
                            inputProps={{ 'aria-label': t('ClientsDetail.placeholderSearch') }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            fullWidth
                            onClick={handleAddClient}
                        >
                            {t('ClientPage.addClient')}
                        </Button>
                    </Box>
                </AppBar>
                <Box px={2} pt={2}>
                    <MobileClientsStatsSection classes={classes} t={t} total={summaryTotal} />
                    <ViewDataEmpty
                        title="Gestiona tus clientes"
                        subtitle="Aquí se mostrarán los clientes que realicen pedidos a través de tu tienda en línea. Tan pronto comiences a recibir pedidos, podrás obtener información detallada de tus clientes."
                        image={ImageDefaultClients}
                    />
                </Box>
                <AddClientModal
                    storeId={storeId}
                    userStoreId={storeData?.default_user_store?.id}
                    isModalOpen={isClientModalOpen}
                    setIsModalOpen={setIsClientModalOpen}
                />
            </Box>
        );
    }

    return (
        <Box className={classes.mobileRoot}>
            <AppBar position="sticky" className={classes.mobileHeader} elevation={0}>
                <Box className={classes.mobileHeaderContent}>
                    <TextField
                        fullWidth
                        placeholder={t('ClientsDetail.placeholderSearch')}
                        variant="outlined"
                        size="small"
                        className={classes.mobileSearchBox}
                        InputProps={{
                            startAdornment: (
                                <Search style={{ marginLeft: 6, marginRight: 8, color: '#999' }} />
                            ),
                        }}
                        value={filter}
                        onChange={handleFilterChange}
                        inputProps={{ 'aria-label': t('ClientsDetail.placeholderSearch') }}
                    />
                    <Box className={classes.mobileButtonGroup}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddOutlinedIcon />}
                            fullWidth
                            onClick={handleAddClient}
                        >
                            {t('ClientPage.addClient')}
                        </Button>
                    </Box>
                </Box>
            </AppBar>

            <Box className={classes.mobileClientList} role="list">
                <MobileClientsStatsSection classes={classes} t={t} total={summaryTotal} />
                <Box mb={2}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        {t('ClientPage.mobileListTitle')}
                    </Typography>
                </Box>

                <Box style={{ minHeight: 4 }}>{gettingData && <LinearProgress />}</Box>

                {rows.map((row) => (
                    <MobileClientCard
                        key={row.id}
                        row={row}
                        classes={classes}
                        storeData={storeData}
                        onNavigateDetail={(r) => setClientToEdit(r)}
                        onWhatsApp={handleSendWhatsapp}
                    />
                ))}

                <Box
                    component="section"
                    id="pagination-section"
                    className={classes.mobilePaginationSection}
                    aria-label="Paginación de clientes"
                >
                    <Typography component="p" className={classes.mobilePaginationSummary}>
                        {totalRows === 0
                            ? `0 ${t('table.clients')}`
                            : `${rangeFrom}–${rangeTo} ${t('table.of')} ${totalRows} ${t('table.clients')}`}
                    </Typography>
                    {totalRows > 0 ? (
                        <Box className={classes.mobilePaginationBar} component="nav">
                            <button
                                type="button"
                                className={classes.mobilePaginationPrevNext}
                                disabled={!canGoPrev}
                                onClick={(e) => handleChangePage(e, page - 1)}
                                aria-label={t('Product.variantsButtonPrev')}
                            >
                                <ChevronLeft
                                    className={`${classes.mobilePaginationPrevNextIcon} ${classes.mobilePaginationPrevNextIconBefore}`}
                                />
                                {t('Product.variantsButtonPrev')}
                            </button>
                            <Box className={classes.mobilePaginationPages}>
                                {buildMobilePaginationItems(page, totalPages).map((item, idx) =>
                                    item.type === 'ellipsis' ? (
                                        <span
                                            key={`e-${idx}`}
                                            className={classes.mobilePaginationEllipsis}
                                            aria-hidden
                                        >
                                            …
                                        </span>
                                    ) : (
                                        <button
                                            key={item.value}
                                            type="button"
                                            className={`${classes.mobilePaginationPageBtn} ${
                                                page + 1 === item.value
                                                    ? classes.mobilePaginationPageBtnActive
                                                    : ''
                                            }`}
                                            onClick={(e) => handleChangePage(e, item.value - 1)}
                                            aria-label={`Página ${item.value}`}
                                            aria-current={page + 1 === item.value ? 'page' : undefined}
                                        >
                                            {item.value}
                                        </button>
                                    )
                                )}
                            </Box>
                            <button
                                type="button"
                                className={classes.mobilePaginationPrevNext}
                                disabled={!canGoNext}
                                onClick={(e) => handleChangePage(e, page + 1)}
                                aria-label={t('Product.variantsButtonNext')}
                            >
                                {t('Product.variantsButtonNext')}
                                <ChevronRight
                                    className={`${classes.mobilePaginationPrevNextIcon} ${classes.mobilePaginationPrevNextIconAfter}`}
                                />
                            </button>
                        </Box>
                    ) : null}
                    <Box className={classes.mobilePaginationMeta}>
                        <label htmlFor="clients-mobile-rows-per-page" className={classes.mobilePaginationSelectLabel}>
                            {t('table.rowsPerPageLabel')}
                        </label>
                        <select
                            id="clients-mobile-rows-per-page"
                            className={classes.mobilePaginationSelect}
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            aria-label={t('table.rowsPerPageLabel')}
                        >
                            {[10, 25, 50].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </Box>
                </Box>
            </Box>

            <AddClientModal
                storeId={storeId}
                userStoreId={storeData?.default_user_store?.id}
                isModalOpen={isClientModalOpen}
                setIsModalOpen={setIsClientModalOpen}
            />
        </Box>
    );
};

const mapStateToProps = ({ auth, clients, common }) => ({
    storeId: auth.storeId,
    initialRowsPerPage: clients.rowsPerPage,
    initialPage: clients.page,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setRowsPerPageState,
            setPageState,
            setClientToEdit,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ClientsPageMobile);
